import { Box, Flex, Switch, Table, TableContainer, Tbody, Td, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import { ActionButton, ActionFab, CopyButton } from '../../../components/ActionButton'
import { ConfirmDialog } from '../../../components/ConfirmDialog'
import { FilterBar, FilterDateRange, FilterSearch } from '../../../components/FilterBar'
import { Icon } from '../../../components/Icon'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import ShortText from '../../../components/ShortText'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { contestPublicUrl, formatApiDate, useAppToast, useHasRole, usePendingRows } from '../../../helpers'
import ContestsService from '../../../services/Contests/ContestsService'
import IFilter, { IContest } from '../../../services/Contests/interfaces'
import { ContestsFormModal } from './ContestsFormModal'

/** `page`/`page_size` ficam de fora: são paginação, não filtro. */
const INITIAL_FILTER = {
  search: '',
  date: { start: '', end: '' }
}

export default function ContestsPage() {
  const modalState = useDisclosure()
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create')
  const hasRole = useHasRole()
  const toast = useAppToast()
  const [contestId, setContestId] = useState<number>(0)
  const contestService = useMemo(() => new ContestsService(), [])
  const [contests, setContests] = useState<IContest[]>([])
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)
  const [deletingId, setDeletingId] = useState(0)
  const [totalPages, setTotalPages] = useState(1)
  const [filter, setFilter] = useState<IFilter>({ ...INITIAL_FILTER, page: 1, page_size: DEFAULT_PAGE_SIZE })
  const isDirty = filter.search !== '' || filter.date.start !== '' || filter.date.end !== ''

  // Trocar um filtro volta para a página 1: filtrar na página 5 pediria a página 5 do
  // resultado novo, quase sempre vazia.
  const updateFilter = (patch: Partial<IFilter>) => setFilter({ ...filter, ...patch, page: 1 })
  const navigate = useNavigate()
  const permissions = {
    viewContests: hasRole('view_contests'),
    addContests: hasRole('add_contests'),
    editContests: hasRole('edit_contests'),
    deleteContests: hasRole('delete_contests'),
    // Both slugs open the evaluation page; view_contest_votes gets it read-only.
    evaluateArtworks: hasRole(['evaluate_contest_artworks', 'view_contest_votes'])
  }

  const fetchContests = async () => {
    setLoading(true)
    setLoadError(false)
    try {
      const response = await contestService.index(filter)
      setContests(response.data)
      setTotalPages(Math.ceil(response.total_filtered / filter.page_size))
    } catch (error) {
      setLoadError(true)
      toast.error('Erro ao carregar os concursos', error)
    } finally {
      setLoading(false)
    }
  }

  const handleDelete = async () => {
    try {
      await contestService.delete(deletingId)
      fetchContests()

      toast.success('Concurso deletado com sucesso!')
    } catch (error: unknown) {
      const axiosError = error as { response?: { data?: { error?: string } } }
      const message = axiosError?.response?.data?.error || 'Erro ao deletar concurso!'

      toast.error(message, error)
    } finally {
      setDeletingId(0)
    }
  }

  // Debounce effect for filters
  // A busca já chega debounced do `FilterSearch`.
  useEffect(() => {
    fetchContests()
  }, [filter])

  const savingRow = usePendingRows()

  const handleToggleGallery = async (contest: IContest) => {
    const next = !contest.show_in_gallery

    // Optimistic update — revert if the request fails.
    setContests((prev) => prev.map((c) => (c.id === contest.id ? { ...c, show_in_gallery: next } : c)))

    try {
      await contestService.updateGalleryVisibility(contest.id, next)
      toast.success(next ? 'Concurso visível na galeria do site.' : 'Concurso oculto da galeria do site.')
    } catch {
      setContests((prev) => prev.map((c) => (c.id === contest.id ? { ...c, show_in_gallery: !next } : c)))
      toast.error('Erro ao atualizar a visibilidade na galeria!')
    }
  }

  return (
    <>
      <Box mb={4}>
        <PageTitle title="Concursos" description="Concursos de arte publicados no site" mb={6} />

        <FilterBar onClear={() => updateFilter(INITIAL_FILTER)} isDirty={isDirty} mb="0">
          <FilterSearch
            label="Nome"
            value={filter.search}
            onChange={(search) => updateFilter({ search })}
            placeholder="Buscar por nome"
          />
          <FilterDateRange label="Data de abertura" value={filter.date} onChange={(date) => updateFilter({ date })} />
        </FilterBar>
      </Box>

      <TableContainer>
        <Table>
          <Thead>
            <Tr>
              <Th>#</Th>
              <Th>Nome</Th>
              <Th>URL</Th>
              <Th>Inscrições</Th>
              <Th>Data de Abertura</Th>
              <Th>Exibir na Galeria</Th>
              <Th>Ações</Th>
            </Tr>
          </Thead>
          <Tbody>
            {loading ? (
              <TableSkeletonRows cols={7} />
            ) : loadError ? (
              <TableErrorRow colSpan={7} entity="concursos" onRetry={fetchContests} />
            ) : contests.length === 0 ? (
              <TableEmptyRow colSpan={7} entity="concurso" />
            ) : (
              contests.map((contest) => {
                const contestFullUrl = contestPublicUrl(contest)
                return (
                  <Tr key={contest.id}>
                    <Td>{contest.id}</Td>
                    <Td>{contest.name}</Td>
                    <Td>
                      <Flex alignItems="center" gap={2}>
                        <ShortText text={contestFullUrl} beforeChars={0} afterChars={`/${contest.slug}`.length} />
                        {/* xs de propósito: acompanha o texto da URL, não é a coluna de ações */}
                        <CopyButton value={contestFullUrl} entity="URL" feminine size="xs" mr={0} />
                      </Flex>
                    </Td>
                    <Td>
                      {contest.paid_applications_count}/{contest.max_registrations}
                    </Td>
                    <Td>{formatApiDate(contest.opening_event_date)}</Td>
                    <Td>
                      <Switch
                        isChecked={contest.show_in_gallery}
                        isDisabled={!permissions.editContests || savingRow.isPending(contest.id)}
                        onChange={() => savingRow.run(contest.id, () => handleToggleGallery(contest))}
                      />
                    </Td>
                    <Td>
                      {permissions.addContests && (
                        <ActionButton
                          action="duplicate"
                          onClick={() => {
                            setModalMode('create')
                            setContestId(contest.id)
                            modalState.onOpen()
                          }}
                        />
                      )}

                      <ActionButton action="view" to={`/contests/${contest.slug}`} />

                      <ActionButton
                        action="view"
                        label="Ver inscritos"
                        colorScheme="purple"
                        icon={<Icon name="lni lni-users" />}
                        onClick={() => navigate(`/contests/applications?contest=${contest.slug}`)}
                      />

                      {permissions.evaluateArtworks && (
                        <ActionButton
                          action="view"
                          label="Avaliação de obras"
                          colorScheme="green"
                          icon={<Icon name="lni lni-star-half" />}
                          onClick={() => navigate(`/contests/artwork-evaluation/${contest.slug}`)}
                        />
                      )}

                      {permissions.editContests && (
                        <ActionButton
                          action="edit"
                          label="Editar concurso"
                          onClick={() => {
                            setModalMode('edit')
                            setContestId(contest.id)
                            modalState.onOpen()
                          }}
                        />
                      )}

                      {permissions.deleteContests && (
                        <ActionButton
                          action="delete"
                          label={
                            contest.paid_applications_count > 0
                              ? 'Não é possível excluir um concurso com inscrições pagas'
                              : 'Excluir concurso'
                          }
                          isDisabled={contest.paid_applications_count > 0}
                          onClick={() => {
                            setDeletingId(contest.id)
                          }}
                        />
                      )}
                    </Td>
                  </Tr>
                )
              })
            )}
          </Tbody>
        </Table>
      </TableContainer>

      <Paginator
        page={filter.page}
        totalPages={totalPages}
        changePage={(page: number) => {
          setFilter({ ...filter, page })
        }}
        pageSize={filter.page_size}
        onPageSizeChange={(size) => {
          setFilter({ ...filter, page_size: size, page: 1 })
        }}
      />

      {permissions.addContests && (
        <ActionFab
          label="Adicionar concurso"
          onClick={() => {
            setModalMode('create')
            modalState.onOpen()
            setContestId(0)
          }}
        />
      )}

      <ContestsFormModal
        isOpen={modalState.isOpen}
        onClose={() => {
          modalState.onClose()
          fetchContests()
        }}
        contestId={contestId}
        mode={modalMode}
      />

      <ConfirmDialog
        isOpen={deletingId > 0}
        onClose={() => {
          fetchContests()
          setDeletingId(0)
        }}
        onConfirm={handleDelete}
        action="delete"
        entity="concurso"
      />
    </>
  )
}
